import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import FundraisingAPI from "Lib/API/Fundraising";
import DonateModal from "./DonateModal";
import DonorWall from "./DonorWall";
import Countdown from "./Countdown";
import "./CampaignPage.scss";
import { bannerCssVars } from "./campaignBanner";

const adjustColor = (hex, amount) => {
  const clamp = v => Math.min(255, Math.max(0, v));
  const n = parseInt(hex.replace('#', ''), 16);
  const r = clamp(((n >> 16) & 0xff) + amount);
  const g = clamp(((n >> 8) & 0xff) + amount);
  const b = clamp((n & 0xff) + amount);
  return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
};

const daysLeft = endDate => {
  if (!endDate) return null;
  const diff = Math.ceil((new Date(endDate) - Date.now()) / 86400000);
  return diff > 0 ? diff : 0;
};

const pct = (raised, goal) =>
  goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : 0;

const makeFmt = orgCountry => n =>
  Number(n || 0).toLocaleString(orgCountry?.local || "en-US", {
    style: "currency",
    currency: orgCountry?.currency || "USD",
    maximumFractionDigits: 0,
  });

const fmtDate = (date, orgCountry) =>
  new Date(date).toLocaleDateString(orgCountry?.local || "en-US", {
    month: "long", day: "numeric", year: "numeric",
  });

const CampaignPage = () => {
  const { slug } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [donateOpen, setDonateOpen] = useState(false);
  const [donateKey, setDonateKey] = useState(0);
  const [donorRefreshKey, setDonorRefreshKey] = useState(0);

  const openDonateModal = () => { setDonateKey(k => k + 1); setDonateOpen(true); };

  useEffect(() => {
    FundraisingAPI.getCampaignBySlug(slug)
      .then(res => setCampaign(res))
      .catch(() => enqueueSnackbar("Campaign not found", { variant: "error" }))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleDonated = result => {
    setCampaign(prev =>
      prev
        ? { ...prev, raised_amount: result.raised_amount, donor_count: result.donor_count }
        : prev
    );
    setDonorRefreshKey(k => k + 1);
  };

  const handleShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: campaign?.title, url });
    } else {
      navigator.clipboard.writeText(url);
      enqueueSnackbar("Link copied!", { variant: "info" });
    }
  };

  if (loading) {
    return (
      <div className="cp-loading">
        <div className="cp-spinner" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="cp-error">
        <div className="cp-error__box">
          <span className="cp-error__icon">🔍</span>
          <h2>Campaign not found</h2>
          <p>This campaign may have ended or the link is incorrect.</p>
        </div>
      </div>
    );
  }

  const percent = pct(campaign.raised_amount, campaign.goal_amount);
  const remaining = daysLeft(campaign.end_date);
  const fmt = makeFmt(campaign.orgCountry);
  const titleInBanner = campaign.show_banner !== false && campaign.banner_color && !campaign.cover_image_url;

  return (
    <div className="cp" style={bannerCssVars}>
      {campaign.show_banner !== false && (campaign.cover_image_url || campaign.org_logo_url || campaign.banner_color) && (
        <div
          className={`cp-hero${titleInBanner ? " cp-hero--with-title" : ""}`}
          style={
            campaign.cover_image_url
              ? { backgroundImage: `url(${campaign.cover_image_url})` }
              : campaign.banner_color
                ? { background: `linear-gradient(135deg, ${adjustColor(campaign.banner_color, -50)} 0%, ${campaign.banner_color} 60%, ${adjustColor(campaign.banner_color, 40)} 100%)` }
                : {}
          }
        >
          {campaign.org_logo_url && campaign.show_org_logo !== false && (
            <img src={campaign.org_logo_url} alt="organization logo" className="cp-hero__logo" />
          )}
          <div className="cp-hero__inner">
            {titleInBanner && (
              <div className="cp-hero__title-wrap">
                <h1 className="cp-hero__title">{campaign.title}</h1>
                {campaign.sub_title && <p className="cp-hero__subtitle">{campaign.sub_title}</p>}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Title section — always on white, always readable */}
      <div className={`cp-title-section${campaign.show_banner === false ? " cp-title-section--no-banner" : ""}`}>
        {campaign.show_banner === false && campaign.org_logo_url && campaign.show_org_logo !== false && (
          <img src={campaign.org_logo_url} alt="organization logo" className="cp-title-section__logo" />
        )}
        {!titleInBanner && (
          <>
            <h1 className="cp-title-section__title">{campaign.title}</h1>
            {campaign.sub_title && <p className="cp-title-section__subtitle">{campaign.sub_title}</p>}
          </>
        )}
        <div className="cp-title-section__meta">
          {remaining !== null && (
            <span className="cp-badge cp-badge--days">
              {remaining > 0 ? `${remaining} days left` : "Campaign ended"}
            </span>
          )}
          {campaign.status === "active" && remaining !== 0 && (
            <span className="cp-badge cp-badge--active">Active</span>
          )}
        </div>
      </div>

      {/* Two-column layout */}
      <div className="cp-layout">

        {/* Left — scrollable content */}
        <div className="cp-content">
          {campaign.description && (
            <div
              className="cp-card-desc"
              dangerouslySetInnerHTML={{ __html: campaign.description }}
            />
          )}

          {campaign.updates?.length > 0 && (
            <section className="cp-section">
              <h2 className="cp-section__title">Updates</h2>
              <div className="cp-updates">
                {[...campaign.updates].reverse().map((u, i) => (
                  <div key={i} className="cp-update">
                    <div className="cp-update__header">
                      <span className="cp-update__dot" />
                      <div>
                        <h4 className="cp-update__title">{u.title}</h4>
                        <span className="cp-update__date">
                          {fmtDate(u.createdAt, campaign.orgCountry)}
                        </span>
                      </div>
                    </div>
                    <p className="cp-update__body">{u.body}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {campaign.show_donor_wall && (
            <section className="cp-section">
              <h2 className="cp-section__title">Our Donors</h2>
              <DonorWall campaignId={campaign._id} refreshTrigger={donorRefreshKey} orgCountry={campaign.orgCountry} />
            </section>
          )}
        </div>

        {/* Right — sticky sidebar */}
        <div className="cp-sidebar">
          {campaign.show_countdown !== false && campaign.end_date && (
            <Countdown endDate={campaign.end_date} />
          )}
          <div className="cp-sidebar__stats">
            <div className="cp-stat">
              <span className="cp-stat__value">{fmt(campaign.raised_amount)}</span>
              <span className="cp-stat__label">raised</span>
            </div>
            <div className="cp-stat">
              <span className="cp-stat__value">{fmt(campaign.goal_amount)}</span>
              <span className="cp-stat__label">goal</span>
            </div>
            <div className="cp-stat">
              <span className="cp-stat__value">{campaign.donor_count}</span>
              <span className="cp-stat__label">donors</span>
            </div>
          </div>

          <div className="cp-progress-bar cp-progress-bar--sidebar">
            <div
              className={`cp-progress-bar__fill${percent >= 100 ? " cp-progress-bar__fill--reached" : ""}`}
              style={{ width: `${percent}%` }}
            />
          </div>
          <div className={`cp-sidebar__pct${percent >= 100 ? " cp-sidebar__pct--reached" : ""}`}>
            {percent >= 100 ? "✓ Goal reached!" : `${percent}% of goal reached`}
          </div>

          {campaign.status === "active" && remaining !== 0 && (
            <button className="cp-btn cp-btn--donate cp-btn--full" onClick={() => openDonateModal()}>
              Make a Donation
            </button>
          )}

          <button className="cp-btn cp-btn--share cp-btn--full-outline" onClick={handleShare}>
            <ShareIcon /> Share
          </button>
        </div>
      </div>

      {/* Floating donate button (mobile) */}
      {campaign.status === "active" && remaining !== 0 && (
        <button className="cp-fab" onClick={() => openDonateModal()}>
          Make a Donation
        </button>
      )}

      {donateOpen && (
        <DonateModal
          key={donateKey}
          campaign={campaign}
          onClose={() => setDonateOpen(false)}
          onDonated={result => handleDonated(result)}
        />
      )}

    </div>
  );
};

const ShareIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

export default CampaignPage;
